import { useState } from 'react';
import { useSelector } from 'react-redux';

import { isContractPristine } from '@app/reducers/current-contract';
import useAgreement from '@app/hooks/use-agreement';
import { isContractEditable } from '@app/components/document-layout-container/helpers';
import { useSaveAgreementHandler } from '@app/components/document-call-to-actions/actions/buttons-call-to-action/hooks/use-save-agreement-handler';
import { showSavedAndNotifiedToast } from '@app/components/document-call-to-actions/actions/save-changes-toasts';
import { PARTIALLY_SIGNED_TOAST_ID } from '@app/components/agreement-loader/partially-signed-toast';
import toast from '@app/components/toasts';
import { T } from '@app/components/translation';
import { DiscardSaveActions } from './buttons-call-to-action';

type Props = {
  agreementId: Oneflow.Document['id'];
};

const OverdueActions = ({ agreementId }: Props) => {
  const agreement = useAgreement(agreementId);
  const [showNotifyParticipantsModal, setShowNotifyParticipantsModal] = useState(false);

  const isPristine = useSelector(isContractPristine);
  const isEditable = isContractEditable(agreement);

  const onSuccess = () => {
    showSavedAndNotifiedToast();
    /**
     * Removes the "partially signed" toast when saving changes.
     * Saving resets signatures, so the document is no longer partially signed.
     * This prevents showing stale toast information to the user.
     */
    toast.remove({
      toastId: PARTIALLY_SIGNED_TOAST_ID,
    });
  };

  const { saveAgreementHandler: onSaveAndNotifyChanges } = useSaveAgreementHandler({
    agreementId,
    onSuccess,
  });

  const menuItems = [
    {
      content: (
        <T
          _str="Save and notify"
          _comment="Menu item for saving and notifying participants"
        />
      ),
      onSelect: () => {
        setShowNotifyParticipantsModal(true);
      },
    },
  ];

  if (!isPristine && isEditable) {
    return (
      <DiscardSaveActions
        agreementId={agreementId}
        isSaveDropdown
        menuItems={menuItems}
        notifyParticipants={{
          setModalOpen: setShowNotifyParticipantsModal,
          modalOpen: showNotifyParticipantsModal,
          onSaveAndNotifyChanges,
        }}
      />
    );
  }

  return null;
};

export default OverdueActions;
